import { createFileRoute } from "@tanstack/react-router";

import { JobApplicationForm } from "@/components/site/JobApplicationForm";
import { PageHero } from "@/components/site/Page";
import { listPublicJobAds } from "@/lib/recruiting.functions";
import { absoluteUrl, seoLinks, ogImage } from "@/lib/seo";
import { safeLoader } from "@/lib/safe-loader";

const path = "/es/empleo";
const title = "Empleo en un colegio sueco en España | SIS Estepona";
const description = "Consulta las vacantes y envía tu candidatura para trabajar en Swedish International School, entre Estepona y Marbella.";

export const Route = createFileRoute("/es/empleo")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl(path) },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "es_ES" },
      ...ogImage(),
    ],
    links: seoLinks(path),
  }),
  loader: () => safeLoader("es.jobs", () => listPublicJobAds(), []),
  component: EmpleoPage,
});

function EmpleoPage() {
  const ads = Route.useLoaderData();

  return (
    <>
      <PageHero
        eyebrow="Trabaja con nosotros"
        title="Vacantes"
        intro="Forma parte de un colegio sueco cercano e internacional, situado entre Estepona y Marbella."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="space-y-5 text-base leading-relaxed text-foreground/90">
          <p>
            Nuestro equipo es la base del colegio. Trabajamos con grupos reducidos, una estrecha
            colaboración entre compañeros y un contacto directo con los alumnos y sus familias.
          </p>
          <h2 className="pt-4 text-2xl font-semibold">Buscamos profesionales que</h2>
          <ul className="list-disc space-y-2 pl-5">
            <li>Tengan formación docente o una preparación pedagógica relevante.</li>
            <li>Quieran adaptar la enseñanza a cada alumno y utilizar herramientas digitales.</li>
            <li>Se sientan cómodos en un entorno donde conviven el sueco, el español y el inglés.</li>
            <li>Valoren la colaboración cercana con las familias, el equipo y la dirección.</li>
          </ul>
          <p>
            Si estás preparando una mudanza, consulta también nuestro{" "}
            <a href="/es/centro-de-recursos" className="text-primary link-quiet">centro de recursos para vivir en España</a>.
          </p>
        </div>
      </section>

      {ads.length > 0 ? (
        <section className="mx-auto max-w-3xl px-5 pb-16">
          <h2 className="text-2xl font-semibold">Vacantes actuales</h2>
          <div className="mt-6 space-y-5">
            {ads.map((ad) => {
              const adTitle = ad.title.es || ad.title.en || ad.title.sv;
              const lead = ad.lead.es || ad.lead.en || ad.lead.sv;
              const body = ad.body.es || ad.body.en || ad.body.sv;
              const fields = ad.customFields.filter((field) => field.public && field.label && field.value);
              return (
                <article key={ad.id} className="rounded-xl border border-border bg-card p-5 shadow-sm">
                  <h3 className="text-xl font-semibold">{adTitle}</h3>
                  <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground">
                    {ad.extent ? <span>{ad.extent}</span> : null}
                    {ad.location ? <span>{ad.location}</span> : null}
                    {ad.applyBy ? <span>Solicita antes del {new Date(ad.applyBy).toLocaleDateString("es-ES", { timeZone: "Europe/Madrid" })}</span> : null}
                  </p>
                  {lead ? <p className="mt-3 text-base leading-relaxed text-foreground/90">{lead}</p> : null}
                  {body ? <p className="mt-3 whitespace-pre-line text-base leading-relaxed text-foreground/80">{body}</p> : null}
                  {ad.requirements.length > 0 ? <><h4 className="mt-4 font-semibold">Requisitos</h4><ul className="mt-1 list-disc space-y-1 pl-5">{ad.requirements.map((item) => <li key={item}>{item}</li>)}</ul></> : null}
                  {ad.merits.length > 0 ? <><h4 className="mt-4 font-semibold">Se valorará</h4><ul className="mt-1 list-disc space-y-1 pl-5">{ad.merits.map((item) => <li key={item}>{item}</li>)}</ul></> : null}
                  {fields.length > 0 ? <dl className="mt-4 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">{fields.map((field) => <div key={field.label} className="flex gap-2"><dt className="text-muted-foreground">{field.label}:</dt><dd>{field.value}</dd></div>)}</dl> : null}
                  <a href="#solicitud-empleo" className="mt-5 inline-flex h-11 items-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground">Enviar candidatura</a>
                </article>
              );
            })}
          </div>
        </section>
      ) : (
        <section className="mx-auto max-w-3xl px-5 pb-16">
          <h2 className="text-2xl font-semibold">Candidatura espontánea</h2>
          <p className="mt-3 text-foreground/80">Aunque no haya una vacante publicada, puedes enviarnos tu candidatura. Las necesidades pueden cambiar durante el curso.</p>
        </section>
      )}

      <section id="solicitud-empleo" className="border-t border-border bg-sand/40">
        <div className="mx-auto max-w-3xl px-5 py-16">
          <JobApplicationForm />
        </div>
      </section>
    </>
  );
}